import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  LinearProgress,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  ExpandMore as ExpandMoreIcon,
  SelfImprovement as MeditationIcon,
  Favorite as LoveIcon,
  Psychology as AnxietyIcon,
  Brightness4 as FearIcon,
  Spa as RelaxIcon,
  Lightbulb as MindfulnessIcon,
  Healing as HealingIcon,
  Air as BreathingIcon
} from '@mui/icons-material';
import PageLayout from './PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { usePlayer } from '../contexts/PlayerContext';

// Categorias de meditação com seus respectivos exercícios
const MEDITATION_CATEGORIES = [
  {
    id: 'anxiety',
    title: 'Ansiedade',
    icon: <AnxietyIcon />,
    meditations: [
      { id: 'anxiety-1', title: 'Respiração para Acalmar', duration: '5:00', audioUrl: '/meditations/anxiety-1.mp3' },
      { id: 'anxiety-2', title: 'Mente Tranquila', duration: '10:00', audioUrl: '/meditations/anxiety-2.mp3' },
      { id: 'anxiety-3', title: 'Libertando Preocupações', duration: '15:00', audioUrl: '/meditations/anxiety-3.mp3' }
    ]
  },
  {
    id: 'relaxation',
    title: 'Relaxamento',
    icon: <RelaxIcon />,
    meditations: [
      { id: 'relax-1', title: 'Relaxamento Profundo', duration: '10:00', audioUrl: '/meditations/relax-1.mp3' },
      { id: 'relax-2', title: 'Sono Tranquilo', duration: '20:00', audioUrl: '/meditations/relax-2.mp3' },
      { id: 'relax-3', title: 'Paz Interior', duration: '15:00', audioUrl: '/meditations/relax-3.mp3' }
    ]
  },
  {
    id: 'fear',
    title: 'Medos',
    icon: <FearIcon />,
    meditations: [
      { id: 'fear-1', title: 'Enfrentando Medos', duration: '10:00', audioUrl: '/meditations/fear-1.mp3' },
      { id: 'fear-2', title: 'Superando Desafios', duration: '15:00', audioUrl: '/meditations/fear-2.mp3' },
      { id: 'fear-3', title: 'Coragem Interior', duration: '12:00', audioUrl: '/meditations/fear-3.mp3' }
    ]
  },
  {
    id: 'mindfulness',
    title: 'Mindfulness',
    icon: <MindfulnessIcon />,
    meditations: [
      { id: 'mind-1', title: 'Atenção Plena', duration: '8:00', audioUrl: '/meditations/mind-1.mp3' },
      { id: 'mind-2', title: 'Momento Presente', duration: '10:00', audioUrl: '/meditations/mind-2.mp3' },
      { id: 'mind-3', title: 'Consciência Corporal', duration: '15:00', audioUrl: '/meditations/mind-3.mp3' }
    ]
  },
  {
    id: 'selfLove',
    title: 'Amor Próprio',
    icon: <LoveIcon />,
    meditations: [
      { id: 'love-1', title: 'Autocuidado', duration: '10:00', audioUrl: '/meditations/love-1.mp3' },
      { id: 'love-2', title: 'Autocompaixão', duration: '12:00', audioUrl: '/meditations/love-2.mp3' },
      { id: 'love-3', title: 'Gratidão', duration: '8:00', audioUrl: '/meditations/love-3.mp3' }
    ]
  },
  {
    id: 'healing',
    title: 'Cura Emocional',
    icon: <HealingIcon />,
    meditations: [
      { id: 'heal-1', title: 'Liberando Emoções', duration: '15:00', audioUrl: '/meditations/heal-1.mp3' },
      { id: 'heal-2', title: 'Cura Interior', duration: '20:00', audioUrl: '/meditations/heal-2.mp3' },
      { id: 'heal-3', title: 'Renovação Energética', duration: '12:00', audioUrl: '/meditations/heal-3.mp3' }
    ]
  },
  {
    id: 'breathing',
    title: 'Respiração',
    icon: <BreathingIcon />,
    meditations: [
      { 
        id: 'breath-1', 
        title: 'Respiração Consciente', 
        duration: '5:00',
        audioUrl: '/sounds/bell.mp3'  // Usando um som que já existe na pasta sounds
      }
    ]
  }
];

export default function Meditations({ customColors }) {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const { 
    isPlaying, 
    currentTrack, 
    progress, 
    handlePlayVideo,
    error: playerError 
  } = usePlayer();
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handlePlay = (meditation) => {
    handlePlayVideo({
      ...meditation,
      id: meditation.id,
      title: meditation.title,
      audioUrl: meditation.audioUrl,
      autoplay: false
    });
  };

  // Mostra o Snackbar quando houver erro no player
  useEffect(() => {
    if (playerError) {
      setOpenSnackbar(true);
    }
  }, [playerError]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <PageLayout
      title="Meditações"
      icon={<MeditationIcon />}
      hideAddButton
      customColors={customColors}
    >
      
      <Grid container spacing={3}>
        {MEDITATION_CATEGORIES.map((category) => (
          <Grid item xs={12} key={category.id}>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ 
                  color: customColors?.primary || theme.palette.primary.main 
                }} />}
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {React.cloneElement(category.icon, {
                    sx: { color: customColors?.primary || theme.palette.primary.main }
                  })}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: customColors?.primary || theme.palette.primary.main 
                    }}
                  >
                    {category.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {category.meditations.map((meditation) => (
                    <ListItem
                      key={meditation.id}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <IconButton
                          onClick={() => handlePlay(meditation)}
                          sx={{
                            color: customColors?.primary || 'primary.main'
                          }}
                        >
                          {currentTrack?.id === meditation.id && isPlaying ? (
                            <PauseIcon />
                          ) : (
                            <PlayArrowIcon />
                          )}
                        </IconButton>
                      </ListItemIcon>
                      <ListItemText
                        primary={meditation.title}
                        secondary={meditation.duration}
                      />
                      {currentTrack?.id === meditation.id && (
                        <Box sx={{ width: '100px', ml: 2 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(progress / (currentTrack?.duration || 1)) * 100}
                            sx={{
                              bgcolor: 'rgba(0,0,0,0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: customColors?.primary || 'primary.main'
                              }
                            }}
                          />
                        </Box>
                      )}
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>

      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          Esta meditação ainda não está disponível
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}